// Angular
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';

// EZ
import { UiLibraryModule } from "@template-ui";
import { DocMeta } from '@template-ui/decorators';
import { setProp } from '@template-ui/helpers';
import { EzBannerConfig, EzBannerService, EzBannerType, EzBannerTypeInput } from '@template-ui/services';
import { EzFormInputType, FormValuesObject, InputConfig } from '@template-ui/types';

type BannerFormSampleValues = FormValuesObject & {
  bannerType: EzBannerTypeInput;
  canClose: boolean;
  message: string;
};

const FORM_BANNER_ID = 'form-banner-sample';

@DocMeta({
  hide: true,
  showcaseTitle: 'Form Built-In Banner',
  showcaseDesc: `
    Demonstrates form-driven usage with:
    - [bannerConfig]
    - [bannerMessage]

    Includes custom banner type examples with sample-level CSS classes.
  `
})
@Component({
  selector: 'ui-banner-form-sample, ez-banner-form-sample',
  standalone: true,
  imports: [UiLibraryModule],
  templateUrl: './ez-banner-form-sample.component.html',
  styleUrls: ['./ez-banner-form-sample.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EzBannerFormSampleComponent {
  private readonly bannerService = inject(EzBannerService);

  // ----------------------------------------------------------------
  //  Public Template-Bound State
  // ----------------------------------------------------------------
  /** Form controls used to drive banner config and message. */
  protected readonly formConfig: InputConfig[] = [
    {
      type: EzFormInputType.SELECT,
      name: 'bannerType',
      label: 'Banner Type',
      row: 1,
      width: '2x',
      options: [
        { value: EzBannerType.REMINDER, viewValue: 'Reminder (default)' },
        { value: EzBannerType.INFO, viewValue: 'Info' },
        { value: EzBannerType.WARNING, viewValue: 'Warning' },
        { value: EzBannerType.ERROR, viewValue: 'Error' },
        { value: 'calm', viewValue: 'Custom: Calm' },
        { value: 'legacy', viewValue: 'Custom: Legacy' },
      ],
    },
    {
      type: EzFormInputType.CHECKBOX,
      name: 'canClose',
      label: 'Show close button',
      row: 1,
      width: '1x',
    },
    {
      type: EzFormInputType.TEXTAREA,
      name: 'message',
      label: 'Banner Message',
      row: 2,
      width: 'fill',
      textAreaRows: 3,
    },
  ];

  /** Signal-backed form values emitted by ez-form. */
  protected readonly formValues = signal<BannerFormSampleValues>({
    bannerType: EzBannerType.REMINDER,
    canClose: false,
    message: 'Pick a type and edit this message to preview the banner.',
  });

  /** Banner config derived from form values. */
  protected readonly bannerConfig = computed<EzBannerConfig>(() => ({
    bannerId: FORM_BANNER_ID,
    bannerType: computed(() => this.formValues().bannerType ?? EzBannerType.REMINDER),
    canClose: computed(() => (this.formValues().canClose)),
  }));

  /** Banner message derived from form values. */
  protected readonly bannerMessage = computed(() => this.formValues().message ?? '');

  /** Service-backed banner message state for disabled/visibility logic. */
  protected readonly currentBanner = this.bannerService.getBannerMessageSignal(FORM_BANNER_ID);

  constructor() {
    effect(() => {
      const serviceMessage = this.currentBanner().message ?? '';
      const localMessage = untracked(() => this.formValues().message ?? '');
      if (localMessage !== serviceMessage) {
        setProp(this.formValues, 'message', serviceMessage);
      }
    }, { allowSignalWrites: true });
  }

  // ----------------------------------------------------------------
  //  Private/Internal State
  // ----------------------------------------------------------------
  private readonly fortunes: readonly string[] = [
    'Small progress today becomes big progress tomorrow.',
    'The cleanest fix is usually the one you can explain in one sentence.',
    'A calm mind catches the bug faster than a rushed one.',
    'Write the test your future self wishes existed.',
    'Simple code is a long-term performance optimization.',
    'Good naming saves more time than good comments.',
  ];

  // ----------------------------------------------------------------
  //  Public Methods (Template Handlers)
  // ----------------------------------------------------------------
  protected showRandomWisdom(event: any): void {
    const index = Math.floor(Math.random() * this.fortunes.length);
    setProp(this.formValues, 'message', this.fortunes[index]);
  }

  protected clearBanner(): void {
    setProp(this.formValues, 'message', '');
  }
}
